import { ModalGeneric, Table, Loader, ButtonDropdown } from 'ypf-pas-general-ui-components';
import PropTypes from 'prop-types';
import { useEffect, useState, useMemo, useContext } from 'react';
import { AiOutlineDownload } from 'react-icons/ai';
import { FaRegFileAlt } from 'react-icons/fa';

import useToast from '../../../../../../hooks/useToast';
import { MAX_DOWNLOAD_SIZE } from '../../../../../../constants/constants';
import {
  getFilesDownloadOptions,
  postDownloadFilesPt
} from '../../../../../../services/workReport';
import {
  FILE_DOWNLOAD_TYPES,
  extensionCanOpenInBrowser
} from '../../../../../../utils/downloadFiles';
import './FilesModal.scss';
import FilesFilters from '../../FilesDetailServiceRequest/FilesFilters/FilesFilters';
import { FilesSdsPtContext } from '../../../../../../context/FilesSdsPtContext';

const FilesModal = ({
  hideModal,
  showModal,
  dataFiles,
  loading,
  setLoading,
  showFilters,
  details,
  noFetch
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isFilesSizeExceeded, setIsFilesSizeExceeded] = useState(false);
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [downloadOptions, setDownloadOptions] = useState([]);
  const { getDataFilesPt } = useContext(FilesSdsPtContext);
  const { setNewToast, closeToast } = useToast();

  const calculateTotalFilesSize = (files) =>
    files.reduce((total, file) => total + file.peso / 1024, 0);

  const fetchData = (value) => {
    const body = {
      tipoAsignacion: details.tipoAsignacion.id,
      filtroArchivosAdjuntos: {
        fechaDesde: details.dateInit,
        fechaHasta: details.dateEnd,
        ubicacion: null
      }
    };

    return getDataFilesPt(details?.id, body, value?.sortBy);
  };
  const handleResetTable = () => {
    setSelectedFiles([]);
    setTotalFileSize(0);
    setIsFilesSizeExceeded(false);
  };
  const downloadFilesOption = async (typeId) => {
    setLoading(true);
    setNewToast({
      type: 'INFO',
      message: 'Descargando, por favor espere.'
    });
    try {
      const idSelectedFiles = [];
      const idParteDiarios = [];
      let response;
      let isOneFile;

      selectedFiles.forEach((file) => {
        idSelectedFiles.push(file.id);
        idParteDiarios.push(file.idParteDiario);
      });

      const isIndividualDownload = [
        FILE_DOWNLOAD_TYPES.INDIVIDUAL,
        FILE_DOWNLOAD_TYPES.BROWSER
      ].includes(typeId);

      isOneFile = selectedFiles.length === 1 ? selectedFiles[0]?.nombre : '';

      if (typeId === FILE_DOWNLOAD_TYPES.COMBINE_PDF) {
        isOneFile = 'document.pdf';
      }

      if (isIndividualDownload && !isOneFile) {
        response = await Promise.all(
          selectedFiles.map((file, index) =>
            postDownloadFilesPt(
              selectedFiles[index].nombre,
              [file.idParteDiario],
              [file.id],
              typeId
            )
          )
        );
      } else {
        response = await postDownloadFilesPt(isOneFile, idParteDiarios, idSelectedFiles, typeId);
      }
      if (response.success) {
        setNewToast({
          type: 'SUCCESS',
          message: `Archivos descargados correctamente.`
        });
      }
    } catch (e) {
      closeToast();
      setNewToast({
        type: 'ERROR',
        message: `No se pudo completar con la descarga.`
      });
    } finally {
      handleResetTable();
      setLoading(false);
    }
  };

  const handleChangeSelect = (item) => {
    const updatedFiles = selectedFiles.find((file) => file.id === item.id)
      ? selectedFiles.filter((file) => file.id !== item.id)
      : [...selectedFiles, item];

    const newTotalSize = calculateTotalFilesSize(updatedFiles);

    setTotalFileSize(newTotalSize);
    setIsFilesSizeExceeded(newTotalSize > MAX_DOWNLOAD_SIZE);
    setSelectedFiles(updatedFiles);
  };

  const handleSelectAll = () => {
    if (selectedFiles.length < dataFiles.length) {
      const newTotalSize = calculateTotalFilesSize(dataFiles);

      setTotalFileSize(newTotalSize);
      setIsFilesSizeExceeded(newTotalSize > MAX_DOWNLOAD_SIZE);
      setSelectedFiles(dataFiles);
    } else {
      handleResetTable();
    }
  };

  const getOptions = async () => {
    const options = await getFilesDownloadOptions();

    setDownloadOptions(options.sort((a, b) => a.id - b.id));
  };

  const isNotOnlyPDFFiles = useMemo(
    () => !!selectedFiles.find((file) => !file.nombre.endsWith('.pdf')),
    [selectedFiles]
  );

  const canOpenInBrowser = useMemo(() => {
    const openInBrowserFiles = selectedFiles.reduce((acc, curr) => {
      if (selectedFiles.includes(curr.id)) return acc;
      if (
        extensionCanOpenInBrowser.some((ext) =>
          curr.nombre.toLowerCase().includes(ext.toLowerCase())
        )
      ) {
        return [...acc, curr.nombre];
      }

      return acc;
    }, []);

    return openInBrowserFiles.length > 1;
  }, [selectedFiles]);

  const optionsParse =
    downloadOptions?.map((option) => ({
      label: option.nombre,
      onClick: () => downloadFilesOption(option.id),
      disabled:
        /**
         * Deshabilitamos la opcion de combinar PDF si los archivos seleccionados contienen otra extension y
         * deshabilitamos el boton de abrir en navegador si es mas de un archivo.
         */
        (option.id === FILE_DOWNLOAD_TYPES.COMBINE_PDF && isNotOnlyPDFFiles) ||
        (option.id === FILE_DOWNLOAD_TYPES.BROWSER && canOpenInBrowser)
    })) || [];
  const isSelectAllChecked = dataFiles.length === selectedFiles.length && dataFiles.length !== 0;
  const columnsFiles = [
    {
      Header: () => (
        <div
          style={{
            textAlign: 'center',
            display: 'flex'
          }}
        >
          <input checked={isSelectAllChecked} onChange={() => handleSelectAll()} type="checkbox" />
          <div
            style={{
              marginLeft: 10
            }}
          >
            Nombre de Archivo
          </div>
        </div>
      ),
      hideSort: true,
      accessor: 'nombre',

      Cell: (values) => {
        const { row } = values;
        const isChecked = selectedFiles.find((file) => file.id === row.original.id);

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyItems: 'center',
              verticalAlign: 'center'
            }}
          >
            <input
              defaultChecked={!!isChecked}
              onChange={() => handleChangeSelect(row.original)}
              type="checkbox"
            />
            <p className="ms-2 mt-3">{row.values.nombre}</p>
          </div>
        );
      }
    },
    {
      Header: 'Tamaño',
      accessor: 'peso',
      hideSort: true,
      style: {
        textAlign: 'center'
      },
      Cell: ({ value }) => {
        const valueMb = Math.round(value / 1024);

        return `${valueMb} kb`;
      }
    },
    {
      Header: 'Fecha de carga',
      accessor: 'fechaSubida',
      hideSort: true,
      style: {
        textAlign: 'center'
      }
    },
    {
      Header: 'Usuario',
      accessor: 'usuario',
      hideSort: true,
      style: {
        textAlign: 'center'
      }
    },
    {
      Header: 'Ubicacion',
      accessor: 'ubicacion',
      style: {
        textAlign: 'center'
      }
    },
    {
      Header: 'Pos./Lin - Fecha',
      accessor: 'linea',
      hideSort: true,
      style: {
        textAlign: 'center'
      }
    }
  ];

  useEffect(() => {
    if (hideModal) {
      handleResetTable();
    }
  }, [hideModal]);

  useEffect(() => {
    getOptions();
  }, []);

  const tableFetchProp = {
    ...(noFetch ? {} : { fetchData })
  };

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={hideModal}
      primaryAction={hideModal}
      primaryActionLabel="Salir"
      show={showModal}
      size="xl"
      title="Archivos Adjuntos"
    >
      <Loader show={loading} text="Cargando..." />
      <div
        className="d-flex flex-row align-items-center justify-content-between"
        style={{
          marginLeft: '10px'
        }}
      >
        <ButtonDropdown
          disabled={!selectedFiles.length || isFilesSizeExceeded}
          label={
            <AiOutlineDownload
              className={selectedFiles.length && !isFilesSizeExceeded ? 'text-primary' : ''}
              size={25}
            />
          }
          options={optionsParse}
          style={{
            color: !selectedFiles.length ? 'gray' : 'primary'
          }}
        />
        {isFilesSizeExceeded && (
          <div className="container-alert">
            <p>
              Has alcanzado un total de {Math.ceil(totalFileSize)}KB. El límite de descarga es de{' '}
              {MAX_DOWNLOAD_SIZE} KB
            </p>
          </div>
        )}
        {showFilters && <FilesFilters details={details} />}
      </div>
      <div className="table-container">
        <Table columns={columnsFiles} data={dataFiles} sortOrder type="light" {...tableFetchProp} />
        {!dataFiles.length ? (
          <div className="d-flex flex-column justify-content-center text-center">
            <span className="m-10 fs-5">No se encontró ninguna coincidencia</span>
            <div className="text-center m-2">
              <FaRegFileAlt size={30} />
            </div>
          </div>
        ) : (
          <></>
        )}{' '}
      </div>
    </ModalGeneric>
  );
};

FilesModal.propTypes = {
  showModal: PropTypes.bool,
  hideModal: PropTypes.func.isRequired,
  dataFiles: PropTypes.oneOfType(PropTypes.oneOfType).isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  showFilters: PropTypes.bool,
  noFetch: PropTypes.bool,
  details: PropTypes.oneOfType(PropTypes.oneOfType)
};

FilesModal.defaultProps = {
  showModal: false,
  showFilters: false,
  noFetch: false,
  details: {}
};
export default FilesModal;
